/** @jsx jsx */

import React, { Fragment as _, useEffect, useRef, FC, Children } from 'react';
import { css, jsx } from '@emotion/core';
import isIE from 'services/isIE';
import { Box } from 'theme-ui';
import arrayToGridLike from 'services/arrayToGridLike';

const Masonry: FC<{
  maxColumns: number;
  maxGridWidth?: number;
  minCellWidth?: number;
  images?: string[];
  uniformHeight?: boolean;
}> = props => {
  // these are not the right vars for the right now
  const {
    maxColumns,
    maxGridWidth = window.innerWidth,
    images = [],
    uniformHeight = false,
    children,
  } = props;

  const container = useRef<HTMLDivElement>(null);
  const cells = useRef<HTMLDivElement[]>([]);
  const imgsLoaded = useRef<boolean>(false);
  //

  useEffect(() => {
    const imgsLoadedProgress: string[] = [];

    if (!images.length) {
      imgsLoaded.current = true;
      return;
    }

    images.forEach((src: string) => {
      const img = new Image();
      img.src = src;

      img.onload = () => {
        imgsLoadedProgress.push(src);

        if (imgsLoadedProgress.length === images.length) {
          imgsLoaded.current = true;
        }
      };
    });
  }, []);

  useEffect(() => {
    if (!container.current) {
      return;
    }

    const cellMinWidth = maxGridWidth / maxColumns;

    let frame;
    let callTickFromResize;

    const tick = (e?: undefined) => {
      if (!container.current) {
        return;
      }

      // console.log('ticking. . .');

      let tallestCell;

      if (uniformHeight) {
        const cellHeights: number[] = cells.current.map(
          (cell: HTMLDivElement) => {
            cell.style.height = 'auto';
            return cell.offsetHeight;
          }
        );

        tallestCell = cells.current[
          cellHeights.indexOf(Math.max(...cellHeights))
        ] as HTMLDivElement;
      }

      let columns = Math.min(
        Math.round(container.current.offsetWidth / cellMinWidth),
        maxColumns
      );

      // fall back to 1 column
      const grid = arrayToGridLike(cells.current, columns || 1);

      grid.forEach((key, rowIndex) => {
        grid[rowIndex].forEach((child, columnIndex) => {
          const currentCell = grid[rowIndex][columnIndex];

          if (!currentCell) {
            return;
          }

          currentCell.style.top = '0px';

          const width = Math.round(container.current!.offsetWidth / columns);

          currentCell.style.left = `${width * columnIndex}px`;
          currentCell.style.width = `${width}px`;

          if (uniformHeight) {
            // we've calculated which cell is the tallest
            // update all other cells to match its height

            const updatedCells = [...cells.current];
            updatedCells.splice(cells.current.indexOf(tallestCell), 1);
            updatedCells.forEach(cell => {
              cell.style.height = `${tallestCell.offsetHeight}px`;
            });
          }

          if (rowIndex > 0) {
            const cellAboveCurrentCell = grid[rowIndex - 1][columnIndex];

            const top =
              cellAboveCurrentCell.offsetTop +
              cellAboveCurrentCell.offsetHeight;

            currentCell.style.top = `${top}px`;
          }
        });
      });

      // setting the height of the container
      const baselines = cells.current.map((cell: HTMLDivElement) => {
        return cell.offsetTop + cell.offsetHeight;
      });
      const baseline = Math.max(...baselines);

      container.current.style.minHeight = `${baseline}px`;

      //
      //
      // in dev we're just gonna keep the ticking going
      // too many cases ( component updates ) to be checking for when to kill it
      // maybe use the slower frameRated setInterval ?

      if (window.dev && window.dev()) {
        clearInterval(frame);
        frame = setInterval(() => tick(), 1000 / 24);

        // frame = requestAnimationFrame(() => tick());
        return;
      }

      // if we resize the browser window we no longer need to ' poll '
      if (e) {
        return;
      }

      clearInterval(frame);
      frame = setInterval(() => tick(), 1000 / 24);

      // frame = requestAnimationFrame(() => tick());
    };

    callTickFromResize = e => {
      tick(e);
    };

    tick();
    //
    window.addEventListener('resize', callTickFromResize);

    return () => {
      // cancelAnimationFrame(frame);
      clearInterval(frame);

      window.removeEventListener('resize', callTickFromResize);
    };
  }, []);

  return (
    <Box
      ref={container}
      sx={{
        position: 'relative',
        width: '100%',
        //
        ...(isIE() && {
          width: 'calc(100vw - 30px)',
          maxWidth: 1100,
        }),
      }}
    >
      {Children.map(children, (child, index) => {
        return (
          <Box
            key={`cell-${index}`}
            ref={(node: HTMLDivElement) => {
              if (!node) {
                return;
              }

              if (!cells.current.includes(node)) {
                cells.current.push(node);
              }
            }}
            sx={{
              position: 'absolute',
            }}
          >
            {child}
          </Box>
        );
      })}
    </Box>
  );
};

export default Masonry;
